"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { User } from "lucide-react"

interface LeaderboardAvatarProps {
  address: string
  ensName?: string
  size?: number
}

export default function LeaderboardAvatar({ address, ensName, size = 28 }: LeaderboardAvatarProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)

  // ENS avatar cache: memory + localStorage
  const AVATAR_CACHE_KEY = 'ensAvatarCacheV1'
  const AVATAR_CACHE_TTL = 24 * 60 * 60 * 1000 // 1 день
  const avatarCache = (typeof window !== 'undefined' ? (window as any).__ensAvatarCache : undefined) || new Map<string, { url: string, ts: number }>()
  if (typeof window !== 'undefined') (window as any).__ensAvatarCache = avatarCache

  useEffect(() => {
    let cancelled = false
    const fetchAvatar = async () => {
      if (!ensName) {
        setIsLoading(false)
        return
      }

      // 1. Проверяем кеш в памяти
      const now = Date.now()
      let cached = avatarCache.get(ensName)
      if (!cached && typeof window !== 'undefined') {
        // 2. Проверяем localStorage
        try {
          const raw = localStorage.getItem(AVATAR_CACHE_KEY)
          if (raw) {
            const obj = JSON.parse(raw)
            if (obj[ensName] && now - obj[ensName].ts < AVATAR_CACHE_TTL) {
              cached = obj[ensName]
              avatarCache.set(ensName, cached)
            }
          }
        } catch {}
      }
      if (cached && now - cached.ts < AVATAR_CACHE_TTL) {
        setAvatarUrl(cached.url)
        setIsLoading(false)
        return
      }

      try {
        // Use direct ENS avatar link. <img> doesn't require CORS.
        const url = `https://metadata.ens.domains/mainnet/avatar/${ensName}`
        if (cancelled) return
        setAvatarUrl(url)
        // Save to cache
        avatarCache.set(ensName, { url, ts: now })
        if (typeof window !== 'undefined') {
          try {
            const raw = localStorage.getItem(AVATAR_CACHE_KEY)
            const obj = raw ? JSON.parse(raw) : {}
            obj[ensName] = { url, ts: now }
            localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(obj))
          } catch {}
        }
      } catch (err) {
        setError(true)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAvatar()
    return () => { cancelled = true }

  }, [ensName])

  // Generate a deterministic color based on the address
  const generateColor = (address: string) => {
    const hash = address.slice(2, 8)
    const hue = Number.parseInt(hash, 16) % 360
    return `hsl(${hue}, 70%, 60%)`
  }

  const bgColor = generateColor(address)

  if (isLoading) {
    return (
      <div className="rounded-full bg-gray-200 animate-pulse" style={{ width: `${size}px`, height: `${size}px` }} />
    )
  }

  if (avatarUrl && !error) {
    return (
      <div
        className="relative rounded-full overflow-hidden"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          padding: "1px", // Create space for the border
        }}
      >
        <div
          className="absolute inset-0 rounded-full border border-black/5 pointer-events-none z-10"
          style={{ width: `${size}px`, height: `${size}px` }}
        />
        <Image
          src={avatarUrl || "/placeholder.svg"}
          alt={ensName || address}
          width={size}
          height={size}
          className="object-cover rounded-full"
          onError={() => setError(true)}
        />
      </div>
    )
  }

  // Fallback to colored circle with first letter or icon
  return (
    <div
      className="flex items-center justify-center rounded-full text-white"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: bgColor,
        fontSize: `${size / 2}px`,
      }}
    >
      {ensName ? ensName.charAt(0).toUpperCase() : <User size={size * 0.6} />}
    </div>
  )
}
